import { useEffect, useRef } from "react";
import { CornerUpLeft, MoreHorizontal } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@cliently/ui";
import { LottieIllustration } from "../LottieIllustration";
import emptyBoxLottie from "../../assets/lottie/empty-box.json";
import type { ChatMessage } from "../../lib/chatApi";

interface MessageListProps {
  messages: ChatMessage[];
  currentClientIds: string[];
  onReply?: (message: ChatMessage) => void;
  onToggleReaction?: (message: ChatMessage, emoji: string, reactedByMe: boolean) => void;
  isTogglingReaction?: boolean;
}

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "🙏"];



const reactionRowForEmoji = (rows: NonNullable<ChatMessage["reactions"]>, emoji: string) => {
  const n = emoji.normalize("NFC");
  return rows.find((r) => r.emoji.normalize("NFC") === n);
}



const parseReplyContent = (content: string): {
  replyToLabel: string;
  replyExcerpt: string;
  body: string;
} | null => {
  const lines = content.split("\n");
  if (!lines[0]?.startsWith("> Réponse à ")) return null;
  const match = lines[0].match(/^> Réponse à (.*?):\s*(.*)$/);
  if (!match) return null;

  const replyToLabel = match[1]?.trim() || "message";
  const replyExcerpt = match[2]?.trim() || "";
  const body = lines.slice(1).join("\n").replace(/^\s+/, "");
  return { replyToLabel, replyExcerpt, body };
}



export const MessageList = ({
  messages,
  currentClientIds,
  onReply,
  onToggleReaction,
  isTogglingReaction = false,
}: MessageListProps) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
        <LottieIllustration
          animationData={emptyBoxLottie}
          size={200}
          className="opacity-90"
        />
        <p className="text-foreground text-sm font-medium">
          Aucun message pour le moment
        </p>
        <p className="text-muted-foreground max-w-sm text-xs">
          Envoyez un message pour contacter votre praticien.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">
      {messages.map((message) => {
        const isMine =
          message.status === "sending" ||
          (message.senderClientId != null &&
            currentClientIds.includes(message.senderClientId));
        const parsedReply = parseReplyContent(message.content);
        const displayedContent = parsedReply ? parsedReply.body : message.content;
        const reactionRows = message.reactions ?? [];
        const hasReactionPill =
          reactionRows.length > 0 && message.status !== "sending";
        return (
          <div
            key={message.id}
            className={[
              "flex",
              isMine ? "justify-end" : "justify-start",
              hasReactionPill ? "mb-2" : "",
            ].join(" ")}
          >
            <div className="relative max-w-[80%]">
              <div
                className={[
                  "rounded-2xl px-3 py-2 text-sm shadow-sm",
                  isMine
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-muted text-foreground rounded-bl-sm",
                  message.status === "sending" ? "opacity-70" : "",
                ].join(" ")}
              >
                <div className="flex gap-1">
                  <div className="min-w-0 flex-1">
                    {!isMine && message.senderLabel ? (
                      <p className="text-muted-foreground mb-1 text-[11px] font-medium">
                        {message.senderLabel}
                      </p>
                    ) : null}
                    {parsedReply && (
                      <div className="mb-2 rounded-md border-l-2 border-current/30 bg-black/10 px-2 py-1 text-xs opacity-85 dark:bg-white/10">
                        <p className="font-medium">
                          Reponse a {parsedReply.replyToLabel}
                        </p>
                        <p className="line-clamp-2 wrap-break-word opacity-90">
                          {parsedReply.replyExcerpt}
                        </p>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap wrap-break-word">{displayedContent}</p>
                    <p className="mt-1 text-[11px] opacity-70">
                      {message.status === "sending"
                        ? "Envoi..."
                        : new Date(message.createdAt).toLocaleTimeString("fr-FR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                    </p>
                  </div>
                  {(onReply || onToggleReaction) && message.status !== "sending" ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={[
                            "-mr-1 -mt-0.5 h-7 w-7 shrink-0 opacity-70 shadow-none transition-opacity hover:opacity-100",
                            isMine
                              ? "text-primary-foreground hover:bg-primary-foreground/15"
                              : "text-foreground hover:bg-foreground/10",
                          ].join(" ")}
                          aria-label="Actions du message"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align={isMine ? "end" : "start"}
                        className="min-w-48"
                        onCloseAutoFocus={(e) => e.preventDefault()}
                      >
                        {onReply ? (
                          <DropdownMenuItem
                            className="gap-2"
                            onSelect={() => onReply(message)}
                          >
                            <CornerUpLeft className="h-4 w-4" />
                            Répondre
                          </DropdownMenuItem>
                        ) : null}
                        {onToggleReaction ? (
                          <>
                            {onReply ? (
                              <div className="bg-border my-1 h-px opacity-60" />
                            ) : null}
                            <p className="text-muted-foreground px-2 py-1 text-[11px] font-medium">
                              Réactions
                            </p>
                            <div className="flex flex-wrap gap-1 px-1 pb-1">
                              {QUICK_REACTIONS.map((emoji) => {
                                const row = reactionRowForEmoji(reactionRows, emoji);
                                const reactedByMe = row?.reactedByMe === true;
                                return (
                                  <DropdownMenuItem
                                    key={`${message.id}-quick-${emoji}`}
                                    aria-pressed={reactedByMe}
                                    className={[
                                      "justify-center rounded-full px-2 py-0.5 text-xs",
                                      reactedByMe
                                        ? "border-2 border-primary bg-primary/15 ring-1 ring-primary/40"
                                        : "border border-dashed border-current/25",
                                    ].join(" ")}
                                    disabled={isTogglingReaction}
                                    onSelect={() =>
                                      onToggleReaction(message, emoji, reactedByMe)
                                    }
                                  >
                                    {emoji}
                                  </DropdownMenuItem>
                                );
                              })}
                            </div>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              </div>
              {hasReactionPill ? (
                <div
                  className={[
                    "absolute z-10 flex max-w-[calc(100%-0.5rem)] items-center gap-0.5 rounded-full border border-border bg-card px-2 py-0.5 text-sm leading-none shadow-sm",
                    isMine ? "bottom-0 right-2 translate-y-1/2" : "bottom-0 left-2 translate-y-1/2",
                  ].join(" ")}
                  role="img"
                  aria-label="Réactions au message"
                >
                  {reactionRows.map((r) => (
                    <span key={r.emoji}>{r.emoji}</span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
